import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import { pageTitle } from "../lib/seo";
import { API_URL, PAYMENT_URL } from "../config";
import {
  FaWhatsapp, FaPhone, FaBox, FaHotel, FaPassport, FaCar,
  FaMapMarkerAlt, FaStar, FaClock, FaCheckCircle, FaTimesCircle,
  FaTimes, FaArrowRight, FaSpinner, FaInfoCircle
} from "react-icons/fa";
import "./QuotationView.css";

const TYPE_LABELS = {
  package: { label: "Holiday Packages", icon: <FaBox />, accent: "#0ea5e9" },
  hotel:   { label: "Hotels",            icon: <FaHotel />, accent: "#8b5cf6" },
  visa:    { label: "Visa Services",     icon: <FaPassport />, accent: "#f59e0b" },
  cab:     { label: "Cabs",              icon: <FaCar />, accent: "#10b981" },
};

const DEFAULT_ADVANCE_AMOUNT = 1000;

function effectivePrice(originalPrice, item) {
  const orig = Number(originalPrice || 0);
  if (item.custom_price !== null && item.custom_price !== undefined) {
    return Number(item.custom_price);
  }
  if (item.discount_pct) {
    return Math.round(orig * (1 - Number(item.discount_pct) / 100));
  }
  return orig;
}

/* Unified accessor: returns the displayable data for a package item.
   Real catalog item: pulls from item.source.
   Custom one-off: pulls from item.custom_data.                    */
function getPackageData(item) {
  if (item.custom_data) {
    return { ...item.custom_data, isCustom: true };
  }
  return { ...(item.source || {}), isCustom: false };
}

export default function QuotationView() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, quote: null, items: { package: [], hotel: [], visa: [], cab: [] }, error: null });
  const [detailItem, setDetailItem] = useState(null); // for the "View full details" modal
  const [showPayForm, setShowPayForm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Quotations aren't publicly readable in the database; the backend
        // looks this one up by its private slug.
        const res = await fetch(`${API_URL}/api/public/quotations/${encodeURIComponent(slug)}`);

        if (cancelled) return;
        if (res.status === 404) { setState({ loading: false, quote: null, items: { package: [], hotel: [], visa: [], cab: [] }, error: "not_found" }); return; }
        if (!res.ok) throw new Error(`Quotation request failed (${res.status})`);
        const { quote, items } = await res.json();

        if (quote.expires_at && new Date(quote.expires_at) < new Date()) {
          setState({ loading: false, quote, items: { package: [], hotel: [], visa: [], cab: [] }, error: "expired" });
          return;
        }

        // Group items by type (custom items use custom_data, real items carry their catalog row in `source`)
        const grouped = { package: [], hotel: [], visa: [], cab: [] };
        for (const it of items || []) {
          if (!grouped[it.item_type]) continue;
          if (it.custom_data || it.source) grouped[it.item_type].push(it);
        }

        if (!cancelled) setState({ loading: false, quote, items: grouped, error: null });
      } catch (err) {
        console.error("QuotationView error:", err);
        if (!cancelled) setState({ loading: false, quote: null, items: { package: [], hotel: [], visa: [], cab: [] }, error: "error" });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const { quote, items } = state;
  const firstImage = quote
    ? (getPackageData(items.package[0] || {}).image || items.hotel[0]?.source?.image)
    : undefined;

  usePageMeta({
    title: quote ? pageTitle(quote.title) : undefined,
    description: quote
      ? `${quote.client_name ? `A custom quotation prepared for ${quote.client_name}. ` : "A custom travel quotation. "}View the full itinerary and pricing.`
      : undefined,
    image: firstImage,
    noindex: true,
  });

  if (state.loading) return <div className="quote-loading">Loading your quotation…</div>;
  if (state.error === "not_found") return <ErrorScreen heading="Quotation not found" body="The link you followed is invalid or has been removed." />;
  if (state.error === "expired")   return <ErrorScreen heading="This quotation has expired" body="Please contact us for an updated quote." />;
  if (state.error)                 return <ErrorScreen heading="Something went wrong" body="Please try again in a moment." />;
  const totalItemCount = items.package.length + items.hotel.length + items.visa.length + items.cab.length;
  const grandTotal =
    items.package.reduce((s, i) => {
      const d = getPackageData(i);
      return s + effectivePrice(d.price, i);
    }, 0) +
    items.hotel.reduce((s, i) => s + effectivePrice(i.source?.price_per_night, i), 0) +
    items.visa.reduce((s, i) => s + effectivePrice(i.source?.price, i), 0) +
    items.cab.reduce((s, i) => s + effectivePrice(i.source?.base_fare, i), 0);

  const wa = (quote.whatsapp_number || "").replace(/\D/g, "");
  const waLink = wa
    ? `https://wa.me/${wa}?text=${encodeURIComponent(`Hi, I'm interested in the quotation: ${quote.title}`)}`
    : null;

  const advanceAmount = Number(quote.advance_amount) > 0 ? Number(quote.advance_amount) : DEFAULT_ADVANCE_AMOUNT;

  return (
    <div className="quote-page">
      {/* Hero */}
      <section className="quote-hero">
        <div className="quote-hero-inner">
          <div className="quote-tag">Custom Quotation</div>
          <h1 className="quote-title">{quote.title}</h1>
          {quote.client_name && (
            <p className="quote-client">Prepared for <strong>{quote.client_name}</strong></p>
          )}
          <div className="quote-meta">
            <span><FaCheckCircle /> {totalItemCount} item{totalItemCount === 1 ? "" : "s"} included</span>
            {quote.expires_at && (
              <span><FaClock /> Valid until {new Date(quote.expires_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}</span>
            )}
          </div>
        </div>
      </section>

      <div className="quote-container">
        {totalItemCount === 0 && (
          <div className="quote-empty-section">
            This quotation has no items yet. Please contact us for details.
          </div>
        )}

        {Object.keys(TYPE_LABELS).map((type) => {
          const list = items[type] || [];
          if (list.length === 0) return null;
          const meta = TYPE_LABELS[type];
          return (
            <section key={type} className="quote-section">
              <div className="quote-section-header" style={{ borderColor: meta.accent }}>
                <span className="quote-section-icon" style={{ background: meta.accent + "1a", color: meta.accent }}>{meta.icon}</span>
                <h2>{meta.label}</h2>
                <span className="quote-section-count">{list.length}</span>
              </div>
              <div className="quote-cards">
                {list.map((it) => (
                  <QuotationItemCard
                    key={it.id}
                    item={it}
                    type={type}
                    accent={meta.accent}
                    onViewDetails={type === "package" ? () => setDetailItem(it) : null}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {/* Total card */}
        {totalItemCount > 0 && (
          <section className="quote-total-card" data-testid="quote-total">
            <div>
              <div className="quote-total-label">Estimated Total</div>
              <div className="quote-total-note">Confirm with ₹{advanceAmount.toLocaleString("en-IN")} advance — balance due before travel.</div>
            </div>
            <div className="quote-total-amount">₹{grandTotal.toLocaleString("en-IN")}</div>
          </section>
        )}

        {/* PAYMENT CTA */}
        {totalItemCount > 0 && (
          <section className="quote-pay-card">
            <div>
              <h3>Confirm this trip</h3>
              <p>Pay a refundable advance of ₹{advanceAmount.toLocaleString("en-IN")} and our team will lock in your dates immediately.</p>
            </div>
            <button
              onClick={() => setShowPayForm(true)}
              className="quote-btn quote-btn-pay"
              data-testid="quote-pay-btn"
            >
              <FaCheckCircle /> Pay ₹{advanceAmount.toLocaleString("en-IN")} advance <FaArrowRight />
            </button>
          </section>
        )}

        {/* Secondary contact CTAs */}
        {(waLink || quote.contact_phone) && (
          <section className="quote-cta-card">
            <div className="quote-cta-inner">
              <h3>Have questions?</h3>
              <p>Reach out — we'll personalise this trip exactly the way you want it.</p>
              <div className="quote-cta-buttons">
                {waLink && (
                  <a href={waLink} target="_blank" rel="noreferrer" className="quote-btn quote-btn-wa" data-testid="quote-cta-whatsapp">
                    <FaWhatsapp /> Chat on WhatsApp
                  </a>
                )}
                {quote.contact_phone && (
                  <a href={`tel:${quote.contact_phone.replace(/\s/g, "")}`} className="quote-btn quote-btn-call" data-testid="quote-cta-call">
                    <FaPhone /> Call {quote.contact_phone}
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        <p className="quote-footer-note">
          Quotation reference: <code>{slug}</code>
        </p>
      </div>

      {/* Rich detail modal for packages */}
      {detailItem && (
        <PackageDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      )}

      {/* Payment form modal */}
      {showPayForm && (
        <PaymentForm
          quote={quote}
          items={items}
          advanceAmount={advanceAmount}
          onClose={() => setShowPayForm(false)}
        />
      )}
    </div>
  );
}

function ErrorScreen({ heading, body }) {
  return (
    <div className="quote-empty">
      <h1>{heading}</h1>
      <p>{body}</p>
      <Link to="/" className="quote-home-link">Visit our website</Link>
    </div>
  );
}

/* ---------- ITEM CARD ---------- */
function QuotationItemCard({ item, type, accent, onViewDetails }) {
  const isPackage = type === "package";
  const data = isPackage ? getPackageData(item) : (item.source || {});
  const original =
    type === "hotel" ? data.price_per_night :
    type === "cab" ? data.base_fare :
    data.price;
  const final = effectivePrice(original, item);
  const hasDiscount = Number(final) !== Number(original || 0) && Number(original) > 0;

  const name = data.name || data.country || "Untitled";
  const subtitle = data.city ? `${data.city}${data.country ? `, ${data.country}` : ""}` : (data.country || "");
  const image = data.image;
  const stars = data.stars;

  return (
    <article className="quote-card">
      {data.isCustom && <div className="quote-custom-badge">CUSTOM</div>}
      {image && <div className="quote-card-image" style={{ backgroundImage: `url(${image})` }} />}
      <div className="quote-card-body">
        <h3 className="quote-card-title">{name}</h3>
        {subtitle && <div className="quote-card-sub"><FaMapMarkerAlt /> {subtitle}</div>}
        {isPackage && data.duration && (
          <div className="quote-card-duration">{data.duration}</div>
        )}
        {type === "hotel" && stars && (
          <div className="quote-card-stars">
            {Array.from({ length: Math.round(Number(stars)) }).map((_, i) => <FaStar key={i} />)}
            <span>{Number(stars).toFixed(1)}</span>
          </div>
        )}
        {item.item_note && <div className="quote-card-note">{item.item_note}</div>}
        <div className="quote-card-price-row">
          {hasDiscount ? (
            <>
              <span className="quote-card-orig">₹{Number(original).toLocaleString("en-IN")}</span>
              <span className="quote-card-final" style={{ color: accent }}>₹{Number(final).toLocaleString("en-IN")}</span>
              {item.discount_pct && (
                <span className="quote-card-badge" style={{ background: accent + "1a", color: accent }}>{item.discount_pct}% OFF</span>
              )}
            </>
          ) : (
            <span className="quote-card-final" style={{ color: accent }}>₹{Number(final).toLocaleString("en-IN")}</span>
          )}
          <span className="quote-card-unit">
            {type === "hotel" && " / night"}
            {type === "cab" && " base fare"}
          </span>
        </div>
        {onViewDetails && (
          <button className="quote-details-btn" onClick={onViewDetails} data-testid="quote-view-details">
            <FaInfoCircle /> View full details
          </button>
        )}
      </div>
    </article>
  );
}

/* ---------- RICH DETAIL MODAL (matches PackageDetailNew layout) ---------- */
function PackageDetailModal({ item, onClose }) {
  const data = getPackageData(item);
  const final = effectivePrice(data.price, item);
  const hasDiscount = Number(final) !== Number(data.price || 0);

  return (
    <div className="quote-modal-overlay" onClick={onClose}>
      <div className="quote-modal" onClick={(e) => e.stopPropagation()}>
        <button className="quote-modal-close" onClick={onClose} data-testid="quote-modal-close"><FaTimes /></button>

        {/* Hero */}
        <div className="quote-modal-hero">
          <img
            src={data.image || "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200"}
            alt={data.name}
            className="quote-modal-hero-img"
          />
          <div className="quote-modal-hero-overlay">
            <div className="quote-modal-hero-content">
              {data.isCustom && <span className="quote-modal-custom-tag">CUSTOM PACKAGE</span>}
              <h1>{data.name}</h1>
              {(data.city || data.country) && <p>{data.city}{data.city && data.country ? ", " : ""}{data.country}</p>}
            </div>
          </div>
        </div>

        <div className="quote-modal-body">
          {/* Overview */}
          <div className="qm-section">
            <h2>Package Overview</h2>
            <div className="qm-meta-grid">
              {data.duration && (
                <div className="qm-meta-box">
                  <div className="qm-meta-label">Duration</div>
                  <div className="qm-meta-value">{data.duration}</div>
                </div>
              )}
              {data.category && (
                <div className="qm-meta-box">
                  <div className="qm-meta-label">Category</div>
                  <div className="qm-meta-value">
                    <span className="qm-cat-badge" style={{ background: data.category === "international" ? "#dbeafe" : "#dcfce7", color: data.category === "international" ? "#1e40af" : "#166534" }}>
                      {data.category === "international" ? "International" : "Domestic"}
                    </span>
                  </div>
                </div>
              )}
              <div className="qm-meta-box">
                <div className="qm-meta-label">Price per person</div>
                <div className="qm-meta-value">
                  {hasDiscount && data.price ? (
                    <>
                      <span style={{ textDecoration: "line-through", color: "#94a3b8", marginRight: 8, fontSize: 16 }}>₹{Number(data.price).toLocaleString("en-IN")}</span>
                      <span style={{ color: "#0ea5e9" }}>₹{Number(final).toLocaleString("en-IN")}</span>
                    </>
                  ) : (
                    <>₹{Number(final).toLocaleString("en-IN")}</>
                  )}
                </div>
              </div>
            </div>
            {data.description && <p className="qm-desc">{data.description}</p>}
            {item.item_note && (
              <div className="qm-note">{item.item_note}</div>
            )}
          </div>

          {/* Itinerary */}
          {data.itinerary && data.itinerary.length > 0 && (
            <div className="qm-section">
              <h2>Day-wise Itinerary</h2>
              <div className="qm-itinerary">
                {data.itinerary.map((day, idx) => (
                  <div key={idx} className="qm-day">
                    <div className="qm-day-num">Day {day.day || idx + 1}</div>
                    {day.title && <h3>{day.title}</h3>}
                    {day.description && <p>{day.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Highlights */}
          {data.highlights && data.highlights.length > 0 && (
            <div className="qm-section">
              <h2>Highlights</h2>
              <ul className="qm-list">
                {data.highlights.map((h, i) => <li key={i}><FaStar style={{ color: "#f59e0b" }} /><span>{h}</span></li>)}
              </ul>
            </div>
          )}

          {/* Inclusions */}
          {data.inclusions && data.inclusions.length > 0 && (
            <div className="qm-section">
              <h2>What's Included</h2>
              <ul className="qm-list">
                {data.inclusions.map((h, i) => <li key={i}><FaCheckCircle style={{ color: "#10b981" }} /><span>{h}</span></li>)}
              </ul>
            </div>
          )}

          {/* Exclusions */}
          {data.exclusions && data.exclusions.length > 0 && (
            <div className="qm-section">
              <h2>What's Not Included</h2>
              <ul className="qm-list">
                {data.exclusions.map((h, i) => <li key={i}><FaTimesCircle style={{ color: "#ef4444" }} /><span>{h}</span></li>)}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- PAYMENT FORM ---------- */
function PaymentForm({ quote, items, advanceAmount, onClose }) {
  const [form, setForm] = useState({
    name: quote.client_name || "",
    phone: quote.client_phone || "",
    email: quote.client_email || "",
    travel_date: "",
    traveller_count: 1,
  });
  const [processing, setProcessing] = useState(false);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function isValid() {
    return form.name.trim() && form.phone.trim() && form.email.trim() && form.travel_date;
  }

  function firstDestination() {
    const p = items.package[0];
    if (p) {
      const d = getPackageData(p);
      return [d.city, d.country].filter(Boolean).join(", ");
    }
    const all = [...items.hotel, ...items.visa, ...items.cab];
    if (all[0]?.source) {
      const s = all[0].source;
      return [s.city, s.country].filter(Boolean).join(", ");
    }
    return "";
  }

  async function handlePay() {
    if (processing) return;
    if (!isValid()) { alert("Please fill all required fields."); return; }
    setProcessing(true);

    try {
      // Use ONE order id for both the saved booking and the payment so the
      // payment-response handler can match and update the correct record, and
      // so the server can validate the charged amount against this booking.
      const orderId = `ORDER_${Date.now()}`;
      const safeAmount = Math.max(1, Math.round(Number(advanceAmount) || 1000));

      // 1. Save the booking
      const bookingPayload = {
        order_id: orderId,
        amount: safeAmount,
        name: form.name,
        phone: form.phone,
        email: form.email,
        traveller_count: Number(form.traveller_count) || 1,
        travellers: [{ name: form.name, age: "" }],
        package_name: `${quote.title} (Quotation)`,
        travel_date: form.travel_date,
        type: "package",
        destination: firstDestination(),
      };

      const res = await fetch(`${API_URL}/api/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bookingPayload),
      });

      if (!res.ok) {
        alert("Could not save your booking. Please try again.");
        setProcessing(false);
        return;
      }

      // 2. Trigger CCAvenue (use same ORDER_ prefix as cart — some
      // CCAvenue accounts validate order_id format strictly)
      const payload = {
        order_id: orderId,
        amount: safeAmount.toFixed(2),
        billing_name: form.name,
        billing_email: form.email,
        billing_tel: form.phone,
      };

      const f = document.createElement("form");
      f.method = "POST";
      f.action = `${PAYMENT_URL}/api/ccavenue-pay`;
      Object.entries(payload).forEach(([k, v]) => {
        const i = document.createElement("input");
        i.type = "hidden"; i.name = k; i.value = v;
        f.appendChild(i);
      });
      document.body.appendChild(f);
      // Don't reset processing — page is about to navigate away
      f.submit();
    } catch (err) {
      console.error("Payment error:", err);
      alert("Payment service unavailable. Please try again.");
      setProcessing(false);
    }
  }

  return (
    <div className="quote-modal-overlay" onClick={processing ? undefined : onClose}>
      {processing && (
        <div className="quote-processing-overlay" data-testid="quote-processing-overlay">
          <div className="quote-processing-modal">
            <FaSpinner className="quote-spin quote-spin-lg" />
            <h3>Processing your booking</h3>
            <p>Please don't close this page. Redirecting to secure payment…</p>
          </div>
        </div>
      )}
      <div className="quote-modal quote-pay-modal" onClick={(e) => e.stopPropagation()}>
        <button className="quote-modal-close" onClick={onClose} disabled={processing}><FaTimes /></button>
        <div className="quote-modal-body" style={{ paddingTop: 32 }}>
          <h2 style={{ marginTop: 0 }}>Confirm with ₹{advanceAmount.toLocaleString("en-IN")} advance</h2>
          <p style={{ color: "#64748b", marginTop: 0 }}>
            Pay a refundable advance to lock in your dates. Our team will reach out to coordinate the rest.
          </p>

          <div className="qm-form">
            <div className="qm-form-row">
              <label>Full name *</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" data-testid="quote-pay-name" />
            </div>
            <div className="qm-form-row">
              <label>Phone *</label>
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98765 43210" data-testid="quote-pay-phone" />
            </div>
            <div className="qm-form-row">
              <label>Email *</label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" data-testid="quote-pay-email" />
            </div>
            <div className="qm-form-row">
              <label>Preferred travel date *</label>
              <input type="date" value={form.travel_date} onChange={(e) => set("travel_date", e.target.value)} min={new Date().toISOString().split("T")[0]} data-testid="quote-pay-date" />
            </div>
            <div className="qm-form-row">
              <label>Number of travelers</label>
              <select value={form.traveller_count} onChange={(e) => set("traveller_count", Number(e.target.value))}>
                {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n} {n === 1 ? "person" : "people"}</option>)}
              </select>
            </div>
          </div>

          <button
            className="quote-btn quote-btn-pay"
            onClick={handlePay}
            disabled={processing || !isValid()}
            data-testid="quote-pay-confirm"
            style={{ width: "100%", marginTop: 20, justifyContent: "center" }}
          >
            {processing ? (<><FaSpinner className="quote-spin" /> Processing…</>) : (<><FaCheckCircle /> Pay ₹{advanceAmount.toLocaleString("en-IN")} now <FaArrowRight /></>)}
          </button>
          <div style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: "#94a3b8" }}>
            Secure payment via CCAvenue
          </div>
        </div>
      </div>
    </div>
  );
}
